import { useCallback, useState } from 'react';
import { Alert, Box, Button, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import { DISCONNECT_RAZORPAY, START_RAZORPAY_CONNECT } from '../../gql/queries';

export interface PaymentConnectionView {
	provider?: string | null;
	status: 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'DISCONNECTED' | string;
	razorpayAccountId?: string | null;
	connectedAt?: string | null;
	statusReason?: string | null;
}

interface DeliveryPaymentsProps {
	locationId: string;
	locationName: string;
	/** Delivery settings exist for the location. Connecting needs them. */
	isConfigured: boolean;
	connection: PaymentConnectionView | null | undefined;
	acceptsCash: boolean;
	onCashChange: (accepts: boolean) => void;
	disabled: boolean;
	/** Re-read the connection after the shop admin finishes in Razorpay's tab. */
	onRefresh: () => void;
}

/** What customers can pay with right now, in their words. */
export function paymentMethodsSummary(acceptsCash: boolean, onlineActive: boolean): string {
	if (acceptsCash && onlineActive) return 'Cash, UPI and card';
	if (onlineActive) return 'UPI and card only';
	if (acceptsCash) return 'Cash only';
	return 'Nothing — customers cannot order';
}

/** The line under the online-payment heading, for each state the connection can be in. */
function connectionDescription(
	connection: PaymentConnectionView | null | undefined,
	active: boolean,
	lapsed: boolean
): string {
	if (active) {
		const since = connection?.connectedAt ? ` · since ${format(new Date(connection.connectedAt), 'd MMM yyyy')}` : '';
		return `Razorpay account ${connection?.razorpayAccountId ?? ''}${since}. Money goes straight to the restaurant's bank account.`;
	}
	if (lapsed) {
		return connection?.statusReason || 'Razorpay no longer accepts this connection.';
	}
	return 'Connect the restaurant’s own Razorpay account. Money goes straight to its bank account.';
}

/**
 * The Payments part of a location's delivery settings: the restaurant's own
 * Razorpay account, and whether it takes cash on delivery.
 *
 * Money from online orders goes straight to the restaurant's bank through its
 * own Razorpay account; SoftDefine never holds it. That is why the connect
 * step happens in Razorpay's own tab, signed in as the restaurant.
 *
 * Two rules from the API are mirrored here so the controls never offer a
 * choice the server will refuse: cash cannot be switched off unless online
 * payment is working, and online payment cannot be disconnected while cash is
 * off. Either would leave customers with no way to pay.
 */
export default function DeliveryPayments({
	locationId,
	locationName,
	isConfigured,
	connection,
	acceptsCash,
	onCashChange,
	disabled,
	onRefresh
}: DeliveryPaymentsProps) {
	const [startConnect, { loading: starting }] = useMutation(START_RAZORPAY_CONNECT);
	const [disconnect, { loading: disconnecting }] = useMutation(DISCONNECT_RAZORPAY);
	const [waitingForTab, setWaitingForTab] = useState(false);

	const status = connection?.status;
	const active = status === 'ACTIVE';
	const lapsed = status === 'EXPIRED' || status === 'REVOKED';
	const busy = disabled || starting || disconnecting;

	const handleConnect = useCallback(async () => {
		// Opened before the request so the browser treats it as a response to
		// the click; a window opened after an await is blocked as a pop-up.
		const tab = window.open('', '_blank');
		try {
			const { data } = await startConnect({ variables: { locationId } });
			const url: string | undefined = data?.startRazorpayConnect;
			if (!url) throw new Error('No link came back.');
			if (tab) {
				tab.location.href = url;
			} else {
				window.location.href = url;
			}
			setWaitingForTab(true);
		} catch (error: any) {
			tab?.close();
			toast.error(`Could not start connecting Razorpay: ${error.message}`);
		}
	}, [locationId, startConnect]);

	const handleDisconnect = useCallback(async () => {
		if (
			!window.confirm(
				`Disconnect Razorpay from ${locationName}? Customers will only be able to pay cash until it is connected again.`
			)
		) {
			return;
		}
		try {
			await disconnect({ variables: { locationId } });
			toast.success(`Razorpay disconnected from ${locationName}.`);
			onRefresh();
		} catch (error: any) {
			toast.error(`Could not disconnect: ${error.message}`);
		}
	}, [disconnect, locationId, locationName, onRefresh]);

	const handleCash = useCallback(
		(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => onCashChange(checked),
		[onCashChange]
	);

	// Cash may always go back on; it may go off only with online payment working.
	const cashSwitchLocked = acceptsCash && !active;

	return (
		<Stack spacing={2}>
			<Box>
				<Typography variant="subtitle2">Payments</Typography>
				<Typography variant="caption" color="text.secondary">
					Customers can pay with: <strong>{paymentMethodsSummary(acceptsCash, active)}</strong>
				</Typography>
			</Box>

			{!acceptsCash && !active ? (
				<Alert severity="error">
					Customers cannot order from {locationName}: cash on delivery is off and online payment is not
					working. Switch cash on, or reconnect Razorpay.
				</Alert>
			) : null}

			<Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
					<Box sx={{ minWidth: 0 }}>
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography variant="body2" fontWeight={600}>
								Online payment — UPI, Google Pay, cards
							</Typography>
							{active ? (
								<Chip size="small" color="success" label="Connected" />
							) : lapsed ? (
								<Chip size="small" color="warning" label="Needs reconnecting" />
							) : (
								<Chip size="small" variant="outlined" label="Not connected" />
							)}
						</Stack>
						<Typography variant="caption" color="text.secondary" display="block">
							{connectionDescription(connection, active, lapsed)}
						</Typography>
						{!isConfigured ? (
							<Typography variant="caption" color="text.secondary" display="block">
								Save the delivery settings first.
							</Typography>
						) : null}
					</Box>
					<Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
						{active ? (
							<Button
								color="error"
								onClick={() => void handleDisconnect()}
								disabled={busy || !acceptsCash}
								title={!acceptsCash ? 'Switch cash on delivery on before disconnecting.' : undefined}
							>
								{disconnecting ? 'Disconnecting…' : 'Disconnect'}
							</Button>
						) : (
							<Button variant="contained" onClick={() => void handleConnect()} disabled={busy || !isConfigured}>
								{starting ? 'Opening Razorpay…' : lapsed ? 'Reconnect Razorpay' : 'Connect Razorpay'}
							</Button>
						)}
					</Stack>
				</Stack>
				{waitingForTab && !active ? (
					<Alert severity="info" sx={{ mt: 2 }} action={<Button onClick={onRefresh}>Check again</Button>}>
						Finish in the Razorpay tab, then come back here.
					</Alert>
				) : null}
			</Box>

			<Box>
				<FormControlLabel
					sx={{ m: 0 }}
					control={<Switch checked={acceptsCash} onChange={handleCash} disabled={busy || cashSwitchLocked} />}
					label={acceptsCash ? 'Cash on delivery accepted' : 'Cash on delivery not accepted'}
				/>
				<Typography variant="caption" color="text.secondary" display="block">
					{cashSwitchLocked
						? 'Connect online payment before switching cash off, or customers would have no way to pay.'
						: 'Saved with the rest of the settings.'}
				</Typography>
			</Box>
		</Stack>
	);
}
